import { useState, useEffect, useRef } from 'react';
import {
  Upload,
  Trash2,
  Image as ImageIcon,
  RefreshCw,
  Copy,
  Check,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import adminApi from '@/lib/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const sectionLabels = {
  hero: 'Section Hero',
  about: 'Qui sommes-nous',
  missions: 'Nos missions',
  ecosystem: 'Écosystème',
  network: 'Réseau national',
  members: 'Logos membres',
  other: 'Autres'
};

const ImagesManager = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [selectedSection, setSelectedSection] = useState('hero');
  const [filterSection, setFilterSection] = useState('all');
  const [copiedId, setCopiedId] = useState(null);
  const fileInputRef = useRef(null);

  const fetchImages = async () => {
    try {
      const response = await adminApi.get(`/images/list`);
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching images:', error);
      toast.error('Erreur lors du chargement des images');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 5 Mo');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('section', selectedSection);

      await adminApi.post(`/images/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Image téléversée avec succès');
      fetchImages();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du téléversement');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async () => {
    if (!imageToDelete) return;

    try {
      await adminApi.delete(`/images/${imageToDelete.id}`);
      toast.success('Image supprimée');
      setDeleteDialogOpen(false);
      setImageToDelete(null);
      fetchImages();
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const copyUrl = (image) => {
    const url = `${BACKEND_URL}${image.url}`;
    navigator.clipboard.writeText(url);
    setCopiedId(image.id);
    toast.success('URL copiée');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredImages = filterSection === 'all'
    ? images
    : images.filter(img => img.section === filterSection);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1e3a8a]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="images-manager">
      {/* Header */}
      <div>
        <h2 className="font-playfair text-2xl font-semibold text-[#0F172A]">
          Gestion des images
        </h2>
        <p className="text-slate-600 font-inter text-sm mt-1">
          {images.length} image(s) téléversée(s)
        </p>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="font-playfair text-lg">Téléverser une image</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="font-inter text-sm">Section</Label>
              <Select value={selectedSection} onValueChange={setSelectedSection}>
                <SelectTrigger className="mt-2" data-testid="upload-section-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(sectionLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-inter text-sm">Image (max 5 Mo)</Label>
              <div className="mt-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="image-upload"
                  data-testid="image-file-input"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  variant="outline"
                  className="w-full h-12"
                  data-testid="upload-image-btn"
                >
                  {uploading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {uploading ? 'Téléversement...' : 'Choisir une image'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={filterSection === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterSection('all')}
          className={filterSection === 'all' ? 'bg-[#1e3a8a]' : ''}
        >
          Toutes ({images.length})
        </Button>
        {Object.entries(sectionLabels).map(([key, label]) => {
          const count = images.filter(img => img.section === key).length;
          if (count === 0) return null;
          return (
            <Button
              key={key}
              variant={filterSection === key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterSection(key)}
              className={filterSection === key ? 'bg-[#1e3a8a]' : ''}
            >
              {label} ({count})
            </Button>
          );
        })}
      </div>

      {/* Images Grid */}
      {filteredImages.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <ImageIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500 font-inter">Aucune image téléversée</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredImages.map((image) => (
            <Card key={image.id} className="overflow-hidden group" data-testid={`image-card-${image.id}`}>
              <div className="aspect-video relative bg-slate-100">
                <img
                  src={`${BACKEND_URL}${image.url}`}
                  alt={image.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => copyUrl(image)}
                    data-testid={`copy-url-${image.id}`}
                  >
                    {copiedId === image.id ? <Check size={16} /> : <Copy size={16} />}
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      setImageToDelete(image);
                      setDeleteDialogOpen(true);
                    }}
                    data-testid={`delete-image-${image.id}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
              <CardContent className="p-3">
                <p className="text-sm font-inter text-slate-700 truncate" title={image.name}>
                  {image.name}
                </p>
                <p className="text-xs text-slate-500 font-inter">
                  {sectionLabels[image.section] || image.section}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette image ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. L'image "{imageToDelete?.name}" sera définitivement supprimée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-image-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ImagesManager;
